import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { cacheVerifier } from '../lib/shiftLock';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Settings, Palette, Building2, Shield, Database, Bell,
  Save, Loader2, RefreshCw, CheckCircle2, AlertTriangle,
  LayoutGrid, ShoppingCart, Boxes, Stethoscope, Brain, Truck, DollarSign, Handshake, Wrench,
  Globe, Languages, Mail, Send, History, RotateCcw, CheckCircle, XCircle, MinusCircle,
  Download, Monitor, KeyRound, UserCog, HardDrive, Sparkles, Trash2, Clock, MessageSquare, Lock, MessageCircle
} from 'lucide-react';
import { getCountryPresets } from '../lib/localeUtils';
import EmergentKeyOverrideCard from '../components/EmergentKeyOverrideCard';
import SignupPolicyCard from '../components/SignupPolicyCard';
import { PortalLinksCard } from '../components/PortalLinksCard';
import { ReferralCard } from '../components/ReferralCard';
import { DesktopAppCard } from '../components/DesktopAppCard';
import { DesktopUpdateBanner } from '../components/DesktopUpdateBanner';
import { enrollQuickSignin, quickSigninSupported } from '../lib/quickSignin';
import { ImageUploadField } from '../components/ImageUploadField';
import { publicImageUrl } from '../lib/media';

const DEFAULT_SETTINGS = {
  business_name: 'DutiLyne',
  tagline: 'AI-Powered Pharmacy Management',
  primary_color: '#14b8a6',
  accent_color: '#e8590c',
  logo_url: '',
  business_address: '',
  contact_phone: '',
  contact_email: '',
  low_stock_alerts: true,
  expiry_alerts: true,
  email_notifications: false,
  weekly_digest_enabled: true,
  alert_days_before_expiry: 30,
  require_prescription_validation: true,
  allow_negative_stock: false,
  auto_reorder_enabled: false,
  default_payment_method: 'cash',
  session_timeout_hours: 24,
  max_login_attempts: 5,
  module_toggles: {
    sales: true,
    inventory: true,
    clinical: true,
    ai_tools: true,
    procurement: true,
    finance: true,
    crm: true,
    admin: true,
    customer_engagement: true,
  },
  locale: {
    country: 'GH',
    currency_code: 'GHS',
    currency_symbol: 'GH₵',
    date_format: 'DD/MM/YYYY',
    number_locale: 'en-GH',
    timezone: 'Africa/Accra',
  }
};

const MODULE_DEFINITIONS = [
  {
    key: 'sales',
    label: 'Sales',
    description: 'Point of Sale terminal and sales history tracking',
    icon: ShoppingCart,
    pages: ['POS', 'Sales History'],
  },
  {
    key: 'inventory',
    label: 'Inventory',
    description: 'Product catalog, bulk import, and stock adjustments',
    icon: Boxes,
    pages: ['Products', 'Bulk Import', 'Stock Adjustments'],
  },
  {
    key: 'clinical',
    label: 'Clinical',
    description: 'Patient management, prescription scanning, and drug interaction checks',
    icon: Stethoscope,
    pages: ['Patients', 'Rx Scanner', 'Drug Check'],
  },
  {
    key: 'ai_tools',
    label: 'AI Tools',
    description: 'AI-powered reorder recommendations and demand forecasting',
    icon: Brain,
    pages: ['AI Reorder', 'Demand Forecast'],
  },
  {
    key: 'procurement',
    label: 'Procurement',
    description: 'Supplier management and purchase order tracking',
    icon: Truck,
    pages: ['Suppliers', 'Purchases'],
  },
  {
    key: 'finance',
    label: 'Finance',
    description: 'Financial accounting, P&L reports, and profitability analysis',
    icon: DollarSign,
    pages: ['Accounting'],
  },
  {
    key: 'crm',
    label: 'CRM & Billing',
    description: 'Insurance providers, corporate clients, and consolidated billing',
    icon: Handshake,
    pages: ['Insurance', 'Corporate', 'Billing'],
  },
  {
    key: 'customer_engagement',
    label: 'Customer Engagement',
    description: 'Unified customer workspace — purchase history, chat, requests, and the pharmacist Clinical Profile',
    icon: MessageSquare,
    pages: ['Customer Workspace', 'Care Chat', 'Clinical Profile'],
  },
  {
    key: 'admin',
    label: 'Administration',
    description: 'User management, multi-pharmacy tenants, and system tools',
    icon: Wrench,
    pages: ['User Mgmt', 'Pharmacies', 'System', 'Settings'],
    locked: true,
    lockedReason: 'Administration cannot be disabled',
  },
];

/**
 * PortalAiModelCard — dedicated picker for the patient-portal med-info /
 * symptom-check model. This is intentionally separate from the staff AI
 * provider because the portal always uses the Emergent Universal Key
 * (never the tenant's own key) and only the model choice is admin-
 * configurable. Anti-hallucination guardrails are hard-coded in
 * routes/portal.py::/ai/med-info regardless of model.
 */
function PortalAiModelCard({ settings, setSettings }) {
  const [models, setModels] = useState([]);
  const current = settings?.portal_ai_model || 'gemini-3.1-pro-preview';

  useEffect(() => {
    api.get('/settings/portal-ai-models').then((r) => {
      setModels(r.data?.models || []);
    }).catch(() => {});
  }, []);

  const setModel = (v) => setSettings((s) => ({ ...s, portal_ai_model: v }));
  const chosen = models.find((m) => m.id === current);

  return (
    <Card data-testid="portal-ai-model-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Brain className="h-4 w-4 text-primary" /> Patient Portal — AI Model
        </CardTitle>
        <CardDescription>
          The model that answers patients&apos; medication questions and symptom checks in the portal. Grounded Gemini models (recommended) consult live sources like MedlinePlus + DailyMed and are much less prone to hallucination.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          <Label>Med-info / symptom-check model</Label>
          <Select value={current} onValueChange={setModel}>
            <SelectTrigger data-testid="portal-ai-model-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {models.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`portal-ai-model-option-${m.id}`}>
                  {m.label}
                </SelectItem>
              ))}
              {models.length === 0 && (
                <>
                  <SelectItem value="gemini-3.1-pro-preview">Gemini 3.1 Pro (recommended · grounded)</SelectItem>
                  <SelectItem value="gemini-3.5-flash">Gemini 3.5 Flash (fast · grounded)</SelectItem>
                  <SelectItem value="gpt-5.5">GPT-5.5 (OpenAI · latest)</SelectItem>
                  <SelectItem value="gpt-5.4">GPT-5.4 (OpenAI)</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
          {chosen && !chosen.supports_grounding && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                This model does not support live search grounding. Answers still go through our JSON-schema safety net, but Gemini models are strongly recommended for medical accuracy.
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}



export default function SettingsPage() {
  const { user } = useAuth();
  const { updateSettings: applyGlobalSettings } = useSettings();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [originalSettings, setOriginalSettings] = useState(DEFAULT_SETTINGS);
  const [hasChanges, setHasChanges] = useState(false);

  const isSuperUser = user?.role === 'super_admin' || user?.role === 'admin';

  const [uploading, setUploading] = useState(false);
  const [aiProviders, setAiProviders] = useState({});
  const [aiTesting, setAiTesting] = useState(false);
  const [aiTestResult, setAiTestResult] = useState(null);

  const [systemInfo] = useState({
    version: '1.0.5',
    database: 'MongoDB',
    api_status: 'healthy',
    last_backup: null
  });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/settings');
      const fetchedSettings = { ...DEFAULT_SETTINGS, ...res.data, module_toggles: { ...DEFAULT_SETTINGS.module_toggles, ...(res.data.module_toggles || {}) }, locale: { ...DEFAULT_SETTINGS.locale, ...(res.data.locale || {}) } };
      setSettings(fetchedSettings);
      setOriginalSettings(fetchedSettings);
    } catch (err) {
      console.error('Failed to load settings', err);
      const savedSettings = localStorage.getItem('pharmxai_settings');
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          setSettings(prev => ({ ...prev, ...parsed }));
          setOriginalSettings(prev => ({ ...prev, ...parsed }));
        } catch (e) {
          console.error('Failed to parse local settings');
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await applyGlobalSettings(settings);
      if (result.success) {
        setOriginalSettings(result.data);
        setSettings(result.data);
        toast.success('Settings saved — branding updated globally');
      } else {
        toast.error(typeof result.error?.response?.data?.detail === 'string' ? result.error.response.data.detail : 'Settings were not saved. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(originalSettings);
    toast.info('Changes discarded');
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateModuleToggle = (moduleKey, enabled) => {
    setSettings(prev => ({
      ...prev,
      module_toggles: { ...prev.module_toggles, [moduleKey]: enabled }
    }));
  };

  const updateLocale = (key, value) => {
    setSettings(prev => ({
      ...prev,
      locale: { ...prev.locale, [key]: value }
    }));
  };

  const applyCountryPreset = (countryCode) => {
    const presets = getCountryPresets();
    const preset = presets[countryCode];
    if (!preset) return;
    setSettings(prev => ({
      ...prev,
      locale: {
        country: countryCode,
        currency_code: preset.code,
        currency_symbol: preset.symbol,
        date_format: preset.dateFormat,
        number_locale: preset.numberLocale,
        timezone: preset.tz,
      }
    }));
  };

  useEffect(() => {
    api.get('/settings/ai-providers').then(res => setAiProviders(res.data || {})).catch(() => {});
  }, []);

  const updateAiConfig = (key, value) => {
    setSettings(prev => ({
      ...prev,
      ai_config: { ...prev.ai_config, [key]: value }
    }));
  };

  const updateAiProviderConfig = (provider, key, value) => {
    setSettings(prev => ({
      ...prev,
      ai_config: {
        ...prev.ai_config,
        [provider]: { ...(prev.ai_config?.[provider] || {}), [key]: value }
      }
    }));
  };

  const handleAiTest = async () => {
    const cfg = settings.ai_config || {};
    const provider = cfg.provider || 'emergent';
    const provCfg = cfg[provider] || {};
    setAiTesting(true);
    setAiTestResult(null);
    try {
      const res = await api.post('/settings/ai-test', {
        provider,
        api_key: provCfg.api_key || '',
        model: provCfg.model || '',
      });
      setAiTestResult(res.data);
      // Surface the friendly toast (with one-click "Switch to Emergent" action)
      // when the provider's quota is exhausted.
      if (res.data && !res.data.success && res.data.code === 'ai_quota_exhausted') {
        const { showAIQuotaToast } = await import('../lib/aiErrorHandler');
        showAIQuotaToast();
      }
    } catch (err) {
      const { handleAIError, isAIQuotaError } = await import('../lib/aiErrorHandler');
      if (isAIQuotaError(err)) {
        setAiTestResult({
          success: false,
          code: 'ai_quota_exhausted',
          message: 'Provider quota exhausted. Switch to Emergent or top up your provider balance.',
        });
        handleAIError(err, 'Connection failed');
      } else {
        setAiTestResult({ success: false, message: err.response?.data?.detail || 'Connection failed' });
      }
    } finally {
      setAiTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure your pharmacy system
          </p>
        </div>
        <div className="flex gap-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset} data-testid="reset-settings-btn">
              Discard
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving || uploading || !hasChanges} className="gap-2" data-testid="save-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Changes
          </Button>
        </div>
      </div>

      <DesktopUpdateBanner />
      <DesktopAppCard />

      <Tabs defaultValue="branding" className="space-y-4">
        <TabsList className={`grid w-full ${isSuperUser ? 'grid-cols-8' : 'grid-cols-7'}`}>
          <TabsTrigger value="branding" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-branding">
            <Palette className="h-4 w-4" /> <span className="hidden sm:inline">Branding</span>
          </TabsTrigger>
          <TabsTrigger value="locale" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-locale">
            <Globe className="h-4 w-4" /> <span className="hidden sm:inline">Locale</span>
          </TabsTrigger>
          <TabsTrigger value="ai" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-ai">
            <Brain className="h-4 w-4" /> <span className="hidden sm:inline">AI</span>
          </TabsTrigger>
          {isSuperUser && (
            <TabsTrigger value="modules" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-modules">
              <LayoutGrid className="h-4 w-4" /> <span className="hidden sm:inline">Modules</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="notifications" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-notifications">
            <Bell className="h-4 w-4" /> <span className="hidden sm:inline">Alerts</span>
          </TabsTrigger>
          <TabsTrigger value="business" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-business">
            <Building2 className="h-4 w-4" /> <span className="hidden sm:inline">Business</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-security">
            <Shield className="h-4 w-4" /> <span className="hidden sm:inline">Security</span>
          </TabsTrigger>
          <TabsTrigger value="system" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-system">
            <Database className="h-4 w-4" /> <span className="hidden sm:inline">System</span>
          </TabsTrigger>
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-4">
          <PortalLinksCard />
          {isSuperUser && <ReferralCard />}
          <Card data-testid="branding-card">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Palette className="h-4 w-4 text-primary" /> Branding & Whitelabeling
              </CardTitle>
              <CardDescription>Customize the look and feel of your pharmacy system. These settings apply to invoices, receipts, and the application header.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Name</Label>
                  <Input
                    value={settings.business_name}
                    onChange={(e) => updateSetting('business_name', e.target.value)}
                    placeholder="Your Pharmacy Name"
                    data-testid="business-name-input"
                  />
                  <p className="text-xs text-muted-foreground">Appears on invoices and receipts</p>
                </div>
                <div className="space-y-2">
                  <Label>Tagline</Label>
                  <Input
                    value={settings.tagline}
                    onChange={(e) => updateSetting('tagline', e.target.value)}
                    placeholder="Your tagline"
                    data-testid="tagline-input"
                  />
                  <p className="text-xs text-muted-foreground">Shown below the business name</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Address</Label>
                  <Input
                    value={settings.business_address || ''}
                    onChange={(e) => updateSetting('business_address', e.target.value)}
                    placeholder="123 Main St, City, Country"
                    data-testid="business-address-input"
                  />
                  <p className="text-xs text-muted-foreground">Full street address shown on documents</p>
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={settings.contact_phone || ''}
                    onChange={(e) => updateSetting('contact_phone', e.target.value)}
                    placeholder="+233 XX XXX XXXX"
                    data-testid="contact-phone-input"
                  />
                  <p className="text-xs text-muted-foreground">Business phone number on invoices</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    value={settings.contact_email || ''}
                    onChange={(e) => updateSetting('contact_email', e.target.value)}
                    placeholder="info@yourpharmacy.com"
                    data-testid="contact-email-input"
                  />
                  <p className="text-xs text-muted-foreground">Business email on invoices and documents</p>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-4">
                <Label>Primary Brand Color</Label>
                <div className="flex items-center gap-4">
                  <div className="space-y-2">
                    <Input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => updateSetting('primary_color', e.target.value)}
                      className="w-20 h-12 p-1 cursor-pointer"
                      data-testid="primary-color-input"
                    />
                  </div>
                  <div className="space-y-2 flex-1">
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => updateSetting('primary_color', e.target.value)}
                      className="w-32 font-mono"
                      placeholder="#14b8a6"
                    />
                  </div>
                  <div className="flex-1">
                    <div 
                      className="h-12 rounded-lg flex items-center justify-center text-white text-sm font-medium shadow-sm"
                      style={{ backgroundColor: settings.primary_color }}
                    >
                      Primary
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Used for buttons, links, navigation highlights, and focus rings</p>
              </div>

              <div className="space-y-4">
                <Label>Accent / Secondary Color</Label>
                <div className="flex items-center gap-4">
                  <div className="space-y-2">
                    <Input
                      type="color"
                      value={settings.accent_color || '#e8590c'}
                      onChange={(e) => updateSetting('accent_color', e.target.value)}
                      className="w-20 h-12 p-1 cursor-pointer"
                      data-testid="accent-color-input"
                    />
                  </div>
                  <div className="space-y-2 flex-1">
                    <Input
                      value={settings.accent_color || '#e8590c'}
                      onChange={(e) => updateSetting('accent_color', e.target.value)}
                      className="w-32 font-mono"
                      placeholder="#e8590c"
                    />
                  </div>
                  <div className="flex-1">
                    <div 
                      className="h-12 rounded-lg flex items-center justify-center text-white text-sm font-medium shadow-sm"
                      style={{ backgroundColor: settings.accent_color || '#e8590c' }}
                    >
                      Accent
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Used for badges, highlights, charts, and secondary call-to-action elements</p>
              </div>

              <div className="border rounded-lg p-3 bg-muted/30">
                <p className="text-xs text-muted-foreground mb-2">Color Palette Preview:</p>
                <div className="flex gap-2">
                  <div className="flex-1 h-10 rounded-md" style={{ backgroundColor: settings.primary_color }} />
                  <div className="flex-1 h-10 rounded-md" style={{ backgroundColor: settings.accent_color || '#e8590c' }} />
                  <div className="flex-1 h-10 rounded-md bg-background border" />
                  <div className="flex-1 h-10 rounded-md bg-muted" />
                  <div className="flex-1 h-10 rounded-md bg-foreground" />
                </div>
                <div className="flex gap-2 mt-1 text-[10px] text-muted-foreground">
                  <span className="flex-1 text-center">Primary</span>
                  <span className="flex-1 text-center">Accent</span>
                  <span className="flex-1 text-center">Background</span>
                  <span className="flex-1 text-center">Muted</span>
                  <span className="flex-1 text-center">Foreground</span>
                </div>
              </div>
              
              <Separator />
              
              <ImageUploadField label="Business / receipt logo" value={settings.logo_url}
                onChange={url => updateSetting('logo_url', url)} endpoint="/upload/logo"
                fields={{ purpose: 'business-logo' }} testId="business-logo" disabled={saving} onBusyChange={setUploading} />

              <div className="border rounded-lg p-4 bg-muted/30">
                <p className="text-xs text-muted-foreground mb-3">Invoice Header Preview:</p>
                <div className="text-center p-4 bg-white dark:bg-gray-900 rounded shadow-sm">
                  {settings.logo_url ? (
                    <img src={publicImageUrl(settings.logo_url)} alt="Logo" className="h-10 max-w-full object-contain mx-auto mb-2" data-testid="business-logo-invoice-preview" onError={(e) => e.target.style.display = 'none'} />
                  ) : (
                    <h2 className="text-xl font-bold" style={{ color: settings.primary_color }}>{settings.business_name}</h2>
                  )}
                  <p className="text-xs text-muted-foreground">{settings.tagline}</p>
                  {settings.business_address && <p className="text-[11px] text-muted-foreground mt-1">{settings.business_address}</p>}
                  <div className="flex items-center justify-center gap-3 mt-1 text-[11px] text-muted-foreground">
                    {settings.contact_phone && <span>{settings.contact_phone}</span>}
                    {settings.contact_phone && settings.contact_email && <span>|</span>}
                    {settings.contact_email && <span>{settings.contact_email}</span>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Localization Tab */}
        <TabsContent value="locale" className="space-y-4">
          <Card data-testid="locale-card">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Globe className="h-4 w-4 text-primary" /> Localization
              </CardTitle>
              <CardDescription>Set your country, currency, and date format. These settings apply globally across POS, invoices, reports, and all financial displays.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Select value={settings.locale?.country || 'GH'} onValueChange={(val) => applyCountryPreset(val)}>
                    <SelectTrigger data-testid="country-selector">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(getCountryPresets()).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(([code, preset]) => (
                        <SelectItem key={code} value={code}>{preset.name} ({preset.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Selecting a country auto-fills currency and format preferences</p>
                </div>
                <div className="space-y-2">
                  <Label>Currency Code</Label>
                  <Input
                    value={settings.locale?.currency_code || 'GHS'}
                    onChange={(e) => updateLocale('currency_code', e.target.value)}
                    className="w-32 font-mono"
                    data-testid="currency-code-input"
                  />
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Currency Symbol</Label>
                  <Input
                    value={settings.locale?.currency_symbol || 'GH₵'}
                    onChange={(e) => updateLocale('currency_symbol', e.target.value)}
                    className="w-24 font-mono text-lg"
                    data-testid="currency-symbol-input"
                  />
                  <p className="text-xs text-muted-foreground">Displayed before amounts (e.g., GH₵, $, £)</p>
                </div>
                <div className="space-y-2">
                  <Label>Date Format</Label>
                  <Select value={settings.locale?.date_format || 'DD/MM/YYYY'} onValueChange={(val) => updateLocale('date_format', val)}>
                    <SelectTrigger data-testid="date-format-selector">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                      <SelectItem value="YYYY/MM/DD">YYYY/MM/DD</SelectItem>
                      <SelectItem value="DD.MM.YYYY">DD.MM.YYYY</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Input
                    value={settings.locale?.timezone || 'Africa/Accra'}
                    onChange={(e) => updateLocale('timezone', e.target.value)}
                    className="font-mono"
                    data-testid="timezone-input"
                  />
                </div>
              </div>

              <Separator />

              <div className="border rounded-lg p-4 bg-muted/30">
                <p className="text-xs text-muted-foreground mb-3 font-medium">Live Preview:</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-3 bg-background rounded-lg border">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Currency</p>
                    <p className="text-lg font-bold mt-1">{settings.locale?.currency_symbol || 'GH₵'} 1,234.56</p>
                    <p className="text-xs text-muted-foreground">{settings.locale?.currency_code || 'GHS'}</p>
                  </div>
                  <div className="p-3 bg-background rounded-lg border">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Date Format</p>
                    <p className="text-lg font-bold mt-1">{(() => {
                      const now = new Date();
                      const dd = String(now.getDate()).padStart(2, '0');
                      const mm = String(now.getMonth() + 1).padStart(2, '0');
                      const yyyy = now.getFullYear();
                      const fmt = settings.locale?.date_format || 'DD/MM/YYYY';
                      if (fmt === 'MM/DD/YYYY') return `${mm}/${dd}/${yyyy}`;
                      if (fmt === 'YYYY-MM-DD') return `${yyyy}-${mm}-${dd}`;
                      if (fmt === 'YYYY/MM/DD') return `${yyyy}/${mm}/${dd}`;
                      if (fmt === 'DD.MM.YYYY') return `${dd}.${mm}.${yyyy}`;
                      return `${dd}/${mm}/${yyyy}`;
                    })()}</p>
                    <p className="text-xs text-muted-foreground">{settings.locale?.date_format || 'DD/MM/YYYY'}</p>
                  </div>
                  <div className="p-3 bg-background rounded-lg border">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Region</p>
                    <p className="text-lg font-bold mt-1">{(() => {
                      const presets = getCountryPresets();
                      return presets[settings.locale?.country]?.name || settings.locale?.country || 'Ghana';
                    })()}</p>
                    <p className="text-xs text-muted-foreground">{settings.locale?.timezone || 'Africa/Accra'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Integration Tab */}
        <TabsContent value="ai" className="space-y-4">
          <PortalAiModelCard settings={settings} setSettings={setSettings} />

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Brain className="h-4 w-4 text-primary" /> AI Provider Configuration
              </CardTitle>
              <CardDescription>Configure which AI provider powers drug interactions, demand forecasting, prescription scanning, and pricing analysis. Use the built-in Emergent AI or bring your own API key.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Active AI Provider</Label>
                <Select value={settings.ai_config?.provider || 'emergent'} onValueChange={v => updateAiConfig('provider', v)}>
                  <SelectTrigger data-testid="ai-provider-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="emergent">Emergent (Default — no key needed)</SelectItem>
                    <SelectItem value="openai">OpenAI (GPT-5.5, GPT-5.4, GPT-5.4 Mini, GPT-5.2)</SelectItem>
                    <SelectItem value="gemini">Google Gemini (3.1 Pro, 3.5 Flash, 3 Flash Preview)</SelectItem>
                    <SelectItem value="deepseek">DeepSeek (Chat, Reasoner)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(settings.ai_config?.provider || 'emergent') === 'emergent' && (
                <>
                  <div className="p-4 rounded-lg bg-muted/50 border">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium">Emergent AI is active</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Using the built-in AI engine. No API key required. Powered by Gemini 3 Flash via Emergent.
                        </p>
                      </div>
                    </div>
                  </div>
                  {user?.is_global_admin && <EmergentKeyOverrideCard />}
                </>
              )}

              {['openai', 'gemini', 'deepseek'].includes(settings.ai_config?.provider) && (() => {
                const prov = settings.ai_config.provider;
                const provConfig = settings.ai_config?.[prov] || {};
                const provMeta = aiProviders[prov] || {};
                const models = provMeta.models || [];
                return (
                  <div className="p-4 rounded-lg border space-y-4">
                    <div className="flex items-center gap-2">
                      <Brain className="h-4 w-4 text-primary" />
                      <p className="text-sm font-semibold capitalize">{prov} Configuration</p>
                    </div>
                    <div className="space-y-2">
                      <Label>API Key</Label>
                      <Input
                        type="password"
                        value={provConfig.api_key || ''}
                        onChange={e => updateAiProviderConfig(prov, 'api_key', e.target.value)}
                        placeholder={`Enter your ${prov} API key`}
                        data-testid="ai-api-key-input"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        {prov === 'openai' && 'Get your key from platform.openai.com/api-keys'}
                        {prov === 'gemini' && 'Get your key from aistudio.google.com/apikey'}
                        {prov === 'deepseek' && 'Get your key from platform.deepseek.com/api_keys'}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Model</Label>
                      <Select value={provConfig.model || models[0] || ''} onValueChange={v => updateAiProviderConfig(prov, 'model', v)}>
                        <SelectTrigger data-testid="ai-model-select"><SelectValue placeholder="Select model" /></SelectTrigger>
                        <SelectContent>
                          {models.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {!provMeta.supports_vision && (
                      <div className="flex items-start gap-2 p-2 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                          This provider does not support image/vision input. Prescription scanning will fall back to the Emergent AI engine.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}

              <Separator />

              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={handleAiTest} disabled={aiTesting} className="gap-1.5" data-testid="ai-test-btn">
                  {aiTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Test Connection
                </Button>
                {aiTestResult && (
                  <div className={`flex items-center gap-1.5 text-sm ${aiTestResult.success ? 'text-emerald-600' : 'text-red-600'}`} data-testid="ai-test-result">
                    {aiTestResult.success ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                    <span className="text-xs">{aiTestResult.message}</span>
                  </div>
                )}
              </div>

              <div className="border rounded-lg p-4 bg-muted/30 space-y-2">
                <p className="text-xs text-muted-foreground font-medium">AI Features powered by this provider:</p>
                <div className="flex flex-wrap gap-2">
                  {['Drug Interactions', 'Prescription Scan', 'Demand Forecast', 'Reorder Suggestions', 'Price Analysis', 'Product Search'].map(f => (
                    <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Modules Tab — Super Admin only */}
        {isSuperUser && (
          <TabsContent value="modules" className="space-y-4">
            <Card data-testid="modules-card">
              <CardHeader>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <LayoutGrid className="h-4 w-4 text-primary" /> ERP Module Control
                </CardTitle>
                <CardDescription>
                  Toggle ERP modules on or off. Disabled modules are hidden from the sidebar for all users. Dashboard and Settings are always accessible.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1">
                {MODULE_DEFINITIONS.map((mod, idx) => {
                  const isEnabled = settings.module_toggles?.[mod.key] !== false;
                  const Icon = mod.icon;
                  return (
                    <div key={mod.key}>
                      {idx > 0 && <Separator className="my-3" />}
                      <div className="flex items-start gap-4 py-1">
                        <div className={`mt-0.5 h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isEnabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold">{mod.label}</p>
                            {mod.locked && (
                              <Badge variant="outline" className="text-[10px]">Always On</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{mod.description}</p>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {mod.pages.map(page => (
                              <Badge key={page} variant="secondary" className="text-[10px] font-normal">
                                {page}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(checked) => updateModuleToggle(mod.key, checked)}
                          disabled={mod.locked}
                          data-testid={`module-toggle-${mod.key}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <Card data-testid="notifications-card">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Bell className="h-4 w-4 text-primary" /> Alert Settings
              </CardTitle>
              <CardDescription>Configure system alerts and notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Low Stock Alerts</p>
                  <p className="text-xs text-muted-foreground">Get notified when products fall below minimum stock</p>
                </div>
                <Switch
                  checked={settings.low_stock_alerts}
                  onCheckedChange={(checked) => updateSetting('low_stock_alerts', checked)}
                  data-testid="low-stock-switch"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Expiry Alerts</p>
                  <p className="text-xs text-muted-foreground">Get notified about products nearing expiry</p>
                </div>
                <Switch
                  checked={settings.expiry_alerts}
                  onCheckedChange={(checked) => updateSetting('expiry_alerts', checked)}
                  data-testid="expiry-switch"
                />
              </div>
              {settings.expiry_alerts && (
                <div className="pl-4 border-l-2 border-primary/30">
                  <Label>Days before expiry to alert</Label>
                  <Input
                    type="number"
                    value={settings.alert_days_before_expiry}
                    onChange={(e) => updateSetting('alert_days_before_expiry', parseInt(e.target.value) || 30)}
                    className="w-24 mt-1"
                    min={1}
                    max={365}
                  />
                </div>
              )}
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Email Notifications</p>
                  <p className="text-xs text-muted-foreground">Send alerts via email (configure SMTP below)</p>
                </div>
                <Switch
                  checked={settings.email_notifications}
                  onCheckedChange={(checked) => updateSetting('email_notifications', checked)}
                  data-testid="email-switch"
                />
              </div>

              <div className="flex items-center justify-between pt-3 border-t">
                <div>
                  <p className="text-sm font-medium">Weekly Admin Digest</p>
                  <p className="text-xs text-muted-foreground">Every Monday — sales, top products, stock & AI spend. Admin accounts only.</p>
                </div>
                <Switch
                  checked={settings.weekly_digest_enabled}
                  onCheckedChange={(checked) => updateSetting('weekly_digest_enabled', checked)}
                  data-testid="weekly-digest-switch"
                />
              </div>
            </CardContent>
          </Card>

          {settings.email_notifications && <SmtpCard />}
          {settings.email_notifications && <AlertHistoryCard />}

          {/* Arkesel SMS — independent of email_notifications, shown to anyone with settings access */}
          <SmsCard />
          <WhatsAppCard />

          {user?.is_global_admin && <PlatformNotificationsCard />}
        </TabsContent>

        {/* Business Rules Tab */}
        <TabsContent value="business" className="space-y-4">
          <Card data-testid="business-card">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" /> Business Rules
              </CardTitle>
              <CardDescription>Configure pharmacy business logic and operational rules</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Require Prescription Validation</p>
                  <p className="text-xs text-muted-foreground">AI must validate prescriptions before checkout</p>
                </div>
                <Switch
                  checked={settings.require_prescription_validation}
                  onCheckedChange={(checked) => updateSetting('require_prescription_validation', checked)}
                  data-testid="rx-validation-switch"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Allow Negative Stock</p>
                  <p className="text-xs text-muted-foreground">Allow sales when stock is zero (backorder)</p>
                </div>
                <Switch
                  checked={settings.allow_negative_stock}
                  onCheckedChange={(checked) => updateSetting('allow_negative_stock', checked)}
                  data-testid="negative-stock-switch"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Auto-Reorder Enabled</p>
                  <p className="text-xs text-muted-foreground">Automatically suggest POs for low stock items</p>
                </div>
                <Switch
                  checked={settings.auto_reorder_enabled}
                  onCheckedChange={(checked) => updateSetting('auto_reorder_enabled', checked)}
                  data-testid="auto-reorder-switch"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-4">
          <SecuritySettings isSuperUser={isSuperUser} />
        </TabsContent>

        {/* System Tab */}
        <TabsContent value="system" className="space-y-4">
          <Card data-testid="system-card">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Database className="h-4 w-4 text-primary" /> System Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">Version</p>
                  <p className="text-sm font-semibold">{systemInfo.version}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">Database</p>
                  <p className="text-sm font-semibold">{systemInfo.database}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">API Status</p>
                  <div className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    <p className="text-sm font-semibold capitalize">{systemInfo.api_status}</p>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">Your Role</p>
                  <Badge variant="outline" className="mt-1">
                    {user?.role || 'Unknown'}
                  </Badge>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Session Timeout (hours)</Label>
                <Input
                  type="number"
                  value={settings.session_timeout_hours}
                  onChange={(e) => updateSetting('session_timeout_hours', parseInt(e.target.value) || 24)}
                  className="w-24"
                  min={1}
                  max={168}
                />
                <p className="text-xs text-muted-foreground">Users will be logged out after this period of inactivity</p>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button variant="outline" className="gap-2" onClick={() => {
                  localStorage.clear();
                  toast.success('Cache cleared');
                }}>
                  <RefreshCw className="h-4 w-4" /> Clear Cache
                </Button>
                <Button variant="outline" className="gap-2" disabled>
                  <Database className="h-4 w-4" /> Export Data
                </Button>
              </div>
            </CardContent>
          </Card>

          <DesktopUpdateCard />

          <BackupRestoreCard />

          <DemoCatalogCard />

          <Card className="border-amber-500/30" data-testid="danger-zone">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-4 w-4" /> Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                These actions are irreversible. Please proceed with caution.
              </p>
              <div className="flex items-center gap-3">
                <Button variant="destructive" className="gap-2" disabled>
                  Reset All Settings
                </Button>
                <Button variant="outline" className="gap-2 text-amber-600 border-amber-600/50" disabled>
                  Clear All Data
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ────────────────────────────────────────────────────────
// SMTP configuration card — lives inside the Alerts tab
// ────────────────────────────────────────────────────────
function SmtpCard() {
  const [cfg, setCfg] = useState({
    smtp_enabled: true,
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_security: 'starttls',
    smtp_from_email: '',
    smtp_from_name: 'DutiLyne Alerts',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/settings/smtp');
        setCfg({ ...cfg, ...data });
      } catch (e) {
        // non-fatal — use defaults
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      // Don't overwrite a stored password with the masked placeholder.
      if (payload.smtp_password === '********') delete payload.smtp_password;
      const { data } = await api.put('/settings/smtp', payload);
      setCfg({ ...cfg, ...data });
      toast.success('SMTP settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save SMTP');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail) {
      toast.error('Enter a destination email first');
      return;
    }
    setTesting(true);
    try {
      await api.post('/settings/smtp/test', { to_email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed — check credentials');
    } finally {
      setTesting(false);
    }
  };

  const runAlertsNow = async (force_weekly = false) => {
    setRunning(true);
    try {
      const { data } = await api.post('/alerts/run-now', null, { params: { force_weekly } });
      if (!data.ran) {
        toast.error(data.reason || 'Alerts not sent');
      } else {
        toast.success(`Alert scan complete — ${data.tasks.join(', ')}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to run alerts');
    } finally {
      setRunning(false);
    }
  };

  const previewWeeklyToMe = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/alerts/preview-weekly-to-me');
      toast.success(`Preview sent to ${data.to_email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setRunning(false);
    }
  };

  const previewBuyerDigestToMe = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/alerts/preview-buyer-digest-to-me');
      toast.success(`Buyer digest preview sent to ${data.to_email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="smtp-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Mail className="h-4 w-4 text-primary" /> SMTP Server
        </CardTitle>
        <CardDescription>Outgoing email for alerts (Gmail, Outlook, SendGrid, or any SMTP server).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
          <div>
            <Label>SMTP host</Label>
            <Input
              placeholder="smtp.gmail.com"
              value={cfg.smtp_host}
              onChange={(e) => set('smtp_host', e.target.value)}
              data-testid="smtp-host"
            />
          </div>
          <div>
            <Label>Port</Label>
            <Input
              type="number"
              value={cfg.smtp_port}
              onChange={(e) => set('smtp_port', parseInt(e.target.value) || 0)}
              data-testid="smtp-port"
            />
          </div>
        </div>

        <div>
          <Label>Security</Label>
          <Select value={cfg.smtp_security} onValueChange={(v) => set('smtp_security', v)}>
            <SelectTrigger data-testid="smtp-security"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="starttls">STARTTLS (port 587 — recommended)</SelectItem>
              <SelectItem value="ssl">SSL / TLS (port 465)</SelectItem>
              <SelectItem value="none">None (insecure, internal networks only)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Username</Label>
            <Input
              placeholder="pharmacy@yourdomain.com"
              value={cfg.smtp_username}
              onChange={(e) => set('smtp_username', e.target.value)}
              data-testid="smtp-username"
            />
          </div>
          <div>
            <Label>Password / App Password</Label>
            <Input
              type="password"
              placeholder="••••••••"
              value={cfg.smtp_password}
              onChange={(e) => set('smtp_password', e.target.value)}
              data-testid="smtp-password"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>From email</Label>
            <Input
              placeholder="alerts@yourdomain.com"
              value={cfg.smtp_from_email}
              onChange={(e) => set('smtp_from_email', e.target.value)}
              data-testid="smtp-from-email"
            />
          </div>
          <div>
            <Label>From name</Label>
            <Input
              placeholder="DutiLyne Alerts"
              value={cfg.smtp_from_name}
              onChange={(e) => set('smtp_from_name', e.target.value)}
              data-testid="smtp-from-name"
            />
          </div>
        </div>

        <div className="text-xs text-muted-foreground rounded-md border bg-muted/40 p-3">
          <b>Gmail tip:</b> SMTP requires an{' '}
          <a href="https://myaccount.google.com/apppasswords"
             target="_blank" rel="noreferrer" className="text-primary underline">
            App Password
          </a>
          {' '}(16 characters, no spaces). Regular Gmail passwords will be rejected.
        </div>

        <div className="flex items-center gap-2 pt-2 border-t">
          <Button onClick={save} disabled={saving} className="gap-2" data-testid="smtp-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save SMTP settings
          </Button>
          <Button variant="ghost" onClick={() => runAlertsNow(false)} disabled={running} className="gap-2" data-testid="alerts-run-now">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
            Run alerts now
          </Button>
          <Button variant="ghost" onClick={() => runAlertsNow(true)} disabled={running} className="gap-2" data-testid="alerts-preview-weekly">
            <History className="h-4 w-4" />
            Preview weekly digest
          </Button>
          <Button variant="ghost" onClick={previewWeeklyToMe} disabled={running} className="gap-2" data-testid="alerts-preview-weekly-to-me">
            <Send className="h-4 w-4" />
            Send digest to me only
          </Button>
          <Button variant="ghost" onClick={previewBuyerDigestToMe} disabled={running} className="gap-2" data-testid="alerts-preview-buyer-digest">
            <Send className="h-4 w-4" />
            Preview buyer digest
          </Button>
          <div className="flex-1" />
          <Input
            placeholder="test@example.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="max-w-[220px]"
            data-testid="smtp-test-to"
          />
          <Button variant="outline" onClick={sendTest} disabled={testing} className="gap-2" data-testid="smtp-test-send">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send test email
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          <b>Run alerts now</b> forces an immediate scan for low-stock, expiring products and AI-budget tiers; otherwise the scheduler runs every hour. Clinical-safety AI calls are never emailed as alerts.
        </p>
      </CardContent>
    </Card>
  );
}


// ────────────────────────────────────────────────────────
// Alert History card — shows the last 30 days of alert digests
// ────────────────────────────────────────────────────────
function AlertHistoryCard() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/alerts/history', { params: { days: 30 } });
      setEntries(data?.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resend = async (entry) => {
    setResending(entry.id);
    try {
      await api.post(`/alerts/resend/${encodeURIComponent(entry.id)}`);
      toast.success(`Re-sending "${entry.subject || entry.type}"…`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally {
      setResending(null);
    }
  };

  const typeLabel = {
    low_stock: 'Low stock',
    expiry: 'Expiry',
    ai_budget: 'AI budget',
    weekly_digest: 'Weekly digest',
  };

  const statusBadge = (s) => {
    switch (s) {
      case 'sent':    return <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30"><CheckCircle className="h-3 w-3 mr-1" /> Sent</Badge>;
      case 'partial': return <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30"><MinusCircle className="h-3 w-3 mr-1" /> Partial</Badge>;
      case 'failed':  return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> Failed</Badge>;
      case 'pending': return <Badge variant="secondary">Pending</Badge>;
      default:        return <Badge variant="outline">{s}</Badge>;
    }
  };

  return (
    <Card data-testid="alert-history-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <History className="h-4 w-4 text-primary" /> Alert History
          </CardTitle>
          <CardDescription>Last 30 days of email alerts. Use "Resend" if a delivery failed and you've since fixed SMTP.</CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="alert-history-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        {loading && entries.length === 0 ? (
          <div className="py-8 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No alerts sent in the last 30 days. When a scan runs, its results will appear here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="alert-history-table">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 pr-3 font-medium">When</th>
                  <th className="py-2 pr-3 font-medium">Type</th>
                  <th className="py-2 pr-3 font-medium">Subject</th>
                  <th className="py-2 pr-3 font-medium">Items</th>
                  <th className="py-2 pr-3 font-medium">Recipients</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pl-3 font-medium text-right"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const when = e.ts ? new Date(e.ts).toLocaleString() : '—';
                  const tlabel = typeLabel[e.type] || e.type;
                  const canResend = e.status === 'failed' || e.status === 'partial';
                  return (
                    <tr key={e.id} className="border-b last:border-0 hover:bg-muted/40 transition-colors">
                      <td className="py-2.5 pr-3 whitespace-nowrap text-xs text-muted-foreground">{when}</td>
                      <td className="py-2.5 pr-3"><span className="font-medium">{tlabel}</span></td>
                      <td className="py-2.5 pr-3 max-w-[260px] truncate" title={e.subject}>{e.subject || '—'}</td>
                      <td className="py-2.5 pr-3 tabular-nums">{e.count ?? '—'}</td>
                      <td className="py-2.5 pr-3 text-xs">
                        <div>{(e.recipients || []).join(', ') || '—'}</div>
                        {e.error && (
                          <div className="text-rose-500 mt-0.5 truncate max-w-[220px]" title={e.error}>{e.error}</div>
                        )}
                      </td>
                      <td className="py-2.5 pr-3">{statusBadge(e.status)}</td>
                      <td className="py-2.5 pl-3 text-right">
                        {canResend && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resend(e)}
                            disabled={resending === e.id}
                            data-testid={`alert-resend-${e.id}`}
                          >
                            {resending === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                            <span className="ml-1 text-xs">Resend</span>
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Desktop App card — only renders inside the Electron shell ──
function DesktopUpdateCard() {
  const isDesktop = typeof window !== 'undefined' && !!window.pharmxai?.checkForUpdates;
  const [checking, setChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  if (!isDesktop) return null;

  const runCheck = async () => {
    setChecking(true);
    try {
      const res = await window.pharmxai.checkForUpdates();
      setLastChecked(new Date());
      if (res?.ok && res.version) {
        toast.success(`Update check OK — latest: v${res.version}. If newer than you, it will download in the background.`);
      } else if (res?.ok) {
        toast.success('You are on the latest version.');
      } else if (res?.reason === 'dev-mode') {
        toast.info('Updates are disabled in development mode.');
      } else {
        toast.error(`Update check failed: ${res?.reason || 'unknown error'}`);
      }
    } catch (e) {
      toast.error(`Update check failed: ${e?.message || e}`);
    } finally {
      setChecking(false);
    }
  };

  return (
    <Card data-testid="desktop-update-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Monitor className="h-4 w-4 text-primary" /> Desktop App
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          DutiLyne checks for updates automatically when it starts and every 6 hours.
          When a new version is ready, it downloads in the background and you&apos;ll be prompted to restart.
          Use the button below to check immediately.
        </p>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="gap-2"
            onClick={runCheck}
            disabled={checking}
            data-testid="check-for-updates-btn"
          >
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {checking ? 'Checking…' : 'Check for Updates'}
          </Button>
          {lastChecked && (
            <span className="text-xs text-muted-foreground" data-testid="last-checked-ts">
              Last checked: {lastChecked.toLocaleTimeString()}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}



// ── Security tab — change own password + admin reset for other users ──
function SecuritySettings({ isSuperUser }) {
  return (
    <div className="space-y-4" data-testid="security-tab-content">
      {isSuperUser && <SignupPolicyCard />}
      <ShiftLockCard isSuperUser={isSuperUser} />
      <ChangeMyPasswordCard />
      {isSuperUser && <AdminResetUserPasswordCard />}
    </div>
  );
}

// Shift lock: my own till PIN, plus the pharmacy-wide auto-lock window.
function ShiftLockCard({ isSuperUser }) {
  const { user, refreshUser } = useAuth();
  const { settings, updateSettings } = useSettings();
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [minutes, setMinutes] = useState(String(settings?.shift_lock_minutes ?? 0));
  const [variance, setVariance] = useState(String(settings?.shift_variance_threshold ?? 5));

  const savePin = async (e) => {
    e.preventDefault();
    if (pin !== confirm) { toast.error('PINs do not match'); return; }
    setBusy(true);
    try {
      await api.post('/auth/shift-pin', { pin });
      cacheVerifier((await api.get('/auth/shift-pin')).data.verifier);
      await enrollQuickSignin({ pin_set: true });
      toast.success(quickSigninSupported()
        ? 'Till PIN saved. You can now sign in on this computer with just your PIN.'
        : 'Till PIN saved. Use the lock button in the header at handover.');
      setPin(''); setConfirm('');
      refreshUser?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save PIN');
    } finally { setBusy(false); }
  };

  const clearPin = async () => {
    setBusy(true);
    try {
      await api.delete('/auth/shift-pin');
      cacheVerifier(null);
      toast.success('Till PIN removed');
      refreshUser?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove PIN');
    } finally { setBusy(false); }
  };

  const saveMinutes = async () => {
    setBusy(true);
    try {
      const res = await updateSettings({ shift_lock_minutes: Number(minutes) || 0 });
      if (!res.success) throw res.error;
      toast.success(Number(minutes) ? `Tills will lock after ${minutes} minutes idle` : 'Auto-lock turned off');
    } catch {
      toast.error('Could not save auto-lock');
    } finally { setBusy(false); }
  };

  const saveVariance = async () => {
    setBusy(true);
    try {
      const res = await updateSettings({ shift_variance_threshold: Number(variance) || 0 });
      if (!res.success) throw res.error;
      toast.success(`Cashiers must explain a drawer more than ${Number(variance) || 0} out`);
    } catch {
      toast.error('Could not save the variance limit');
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="shift-lock-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Lock className="h-4 w-4 text-primary" /> Shift Lock
        </CardTitle>
        <CardDescription>
          Cover the till at handover without signing out — your session stays open and a
          4-digit PIN brings it back. &quot;Switch user&quot; on the lock screen does a full sign-out.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <form onSubmit={savePin} className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {user?.pin_set ? 'Change my PIN' : 'Create my PIN'}
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">New 4-digit PIN</Label>
              <Input
                type="password" inputMode="numeric" maxLength={4} value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                placeholder="••••" data-testid="shift-pin-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Confirm PIN</Label>
              <Input
                type="password" inputMode="numeric" maxLength={4} value={confirm}
                onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ''))}
                placeholder="••••" data-testid="shift-pin-confirm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || pin.length !== 4} data-testid="shift-pin-save">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save PIN
            </Button>
            {user?.pin_set && (
              <Button type="button" variant="outline" onClick={clearPin} disabled={busy} data-testid="shift-pin-clear">
                Remove PIN
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Obvious PINs like 0000 or 1234 are rejected. Five wrong tries signs the till out.
          </p>
        </form>

        {isSuperUser && (
          <div className="border-t pt-4 space-y-2">
            <Label className="text-xs">Auto-lock after (minutes of inactivity)</Label>
            <div className="flex gap-2 max-w-xs">
              <Input
                type="number" min={0} max={240} value={minutes}
                onChange={(e) => setMinutes(e.target.value)} data-testid="shift-lock-minutes"
              />
              <Button onClick={saveMinutes} disabled={busy} data-testid="shift-lock-minutes-save">Save</Button>
            </div>
            <p className="text-xs text-muted-foreground">
              0 turns auto-lock off and leaves the header lock button for manual handovers.
            </p>
          </div>
        )}
        {isSuperUser && (
          <div className="border-t pt-4 space-y-2">
            <Label className="text-xs">Cash count variance allowed before a note is required</Label>
            <div className="flex gap-2 max-w-xs">
              <Input
                type="number" min={0} step="0.5" value={variance}
                onChange={(e) => setVariance(e.target.value)} data-testid="shift-variance-threshold"
              />
              <Button onClick={saveVariance} disabled={busy} data-testid="shift-variance-save">Save</Button>
            </div>
            <p className="text-xs text-muted-foreground">
              A drawer further out than this at handover can only be saved with a short explanation.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ChangeMyPasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error('New passwords do not match'); return; }
    if (next.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setBusy(true);
    try {
      await api.post('/users/me/change-password', { current_password: current, new_password: next });
      toast.success('Password updated. Use the new password on your next login.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update password');
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="change-my-password-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-primary" /> Change My Password
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3 max-w-md" autoComplete="off">
          <div>
            <Label className="text-xs">Current password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="my-current-pwd" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">New password</Label>
              <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} data-testid="my-new-pwd" />
            </div>
            <div>
              <Label className="text-xs">Confirm new</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} data-testid="my-confirm-pwd" />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="gap-2" data-testid="my-change-pwd-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Update Password
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function AdminResetUserPasswordCard() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/users?limit=200');
        if (alive) setUsers(data?.users || data?.items || data || []);
      } catch (e) { /* noop */ }
      finally { if (alive) setLoadingUsers(false); }
    })();
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedId) { toast.error('Pick a user'); return; }
    if (newPwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    const target = users.find((u) => u.user_id === selectedId);
    if (!window.confirm(`Reset password for ${target?.email}? They will be logged out and need the new password to sign in.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${selectedId}/reset-password`, { new_password: newPwd });
      toast.success(`Password reset for ${data.email}. Share the new password with them securely.`);
      setNewPwd(''); setSelectedId('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="admin-reset-pwd-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <UserCog className="h-4 w-4 text-primary" /> Reset Another User's Password
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          For when a staff member forgets their password. The user will be signed out and must use the new password.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3 max-w-md" autoComplete="off">
          <div>
            <Label className="text-xs">User</Label>
            <Select value={selectedId} onValueChange={setSelectedId} disabled={loadingUsers}>
              <SelectTrigger data-testid="reset-user-select">
                <SelectValue placeholder={loadingUsers ? 'Loading users…' : 'Select a user'} />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name || u.email} - {u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">New temporary password</Label>
            <Input type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required minLength={8}
                   placeholder="At least 8 characters" data-testid="reset-new-pwd" />
            <p className="text-xs text-muted-foreground mt-1">
              Tell the user to change this immediately after logging in.
            </p>
          </div>
          <Button type="submit" disabled={busy || !selectedId} className="gap-2" variant="destructive" data-testid="admin-reset-pwd-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCog className="h-4 w-4" />}
            Reset Password
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}




// ── Backup & Restore card (Settings -> System) ──
function BackupRestoreCard() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/system/backups');
      setList(Array.isArray(data) ? data : (data?.backups || []));
    } catch (e) {
      // noop - users without permission won't see this card
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const backupNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/system/backup');
      if (data?.status === 'completed') {
        toast.success(`Backup created (${data.size_mb || 0} MB)`);
      } else {
        toast.error(data?.error || 'Backup failed');
      }
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backup failed');
    } finally { setBusy(false); }
  };

  const restore = async (backup) => {
    if (!window.confirm(
      `Restore backup from ${new Date(backup.created_at).toLocaleString()}?\n\n` +
      `This will REPLACE all current data with the contents of this backup. ` +
      `Current data will be lost. You will be signed out and need to log in again.`
    )) return;
    setRestoringId(backup.backup_id);
    try {
      await api.post(`/system/backups/${backup.backup_id}/restore`);
      toast.success('Restore complete. You will be signed out in a moment.');
      setTimeout(() => { localStorage.clear(); window.location.href = '/login'; }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally { setRestoringId(null); }
  };

  return (
    <Card data-testid="backup-restore-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <HardDrive className="h-4 w-4 text-primary" /> Backup & Restore
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Backups are stored on this PC. Copy the latest one to a USB stick weekly.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Button onClick={backupNow} disabled={busy} className="gap-2" data-testid="backup-now-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Backup Now
          </Button>
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading} className="gap-1.5">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="backup-list-table">
            <thead className="bg-muted/50 text-left text-xs">
              <tr>
                <th className="p-2.5 font-medium">When</th>
                <th className="p-2.5 font-medium">Size</th>
                <th className="p-2.5 font-medium">By</th>
                <th className="p-2.5 font-medium">Status</th>
                <th className="p-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="p-3 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && list.length === 0 && (
                <tr><td colSpan={5} className="p-3 text-center text-muted-foreground">
                  No backups yet. Click <span className="font-medium">Backup Now</span> to create one.
                </td></tr>
              )}
              {!loading && list.map((b) => (
                <tr key={b.backup_id} className="border-t hover:bg-muted/30">
                  <td className="p-2.5 flex items-center gap-1.5">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    {new Date(b.created_at).toLocaleString()}
                  </td>
                  <td className="p-2.5 tabular-nums">{b.size_mb ? `${b.size_mb} MB` : '—'}</td>
                  <td className="p-2.5 text-xs">{b.created_by_name || b.created_by || '—'}</td>
                  <td className="p-2.5">
                    {b.status === 'completed' ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Completed</Badge>
                    ) : (
                      <Badge variant="destructive">{b.status || 'unknown'}</Badge>
                    )}
                  </td>
                  <td className="p-2.5 text-right">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => restore(b)}
                      disabled={b.status !== 'completed' || restoringId === b.backup_id}
                      className="gap-1.5 text-xs h-7"
                      data-testid={`restore-${b.backup_id}-btn`}
                    >
                      {restoringId === b.backup_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                      Restore
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}


// ── Demo catalog loader (Settings -> System) ──
function DemoCatalogCard() {
  const [info, setInfo] = useState({ available: 0, existing_in_tenant: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/desktop/demo-catalog/preview');
      setInfo(data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const load = async () => {
    if (info.existing_in_tenant > 0 && !window.confirm(
      `You already have ${info.existing_in_tenant} products in your catalog. ` +
      `Loading the demo will ADD ${info.available} more (any with the same barcode are skipped). Continue?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post('/desktop/demo-catalog/load');
      toast.success(`Loaded ${data.inserted} demo products${data.skipped ? `, skipped ${data.skipped}` : ''}.`);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load demo catalog');
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm('Remove all demo products from your catalog? Your real products are unaffected.')) return;
    setBusy(true);
    try {
      const { data } = await api.delete('/desktop/demo-catalog');
      toast.success(`Removed ${data.deleted} demo products`);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not clear demo data');
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="demo-catalog-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" /> Sample Product Catalog
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          {loading ? 'Loading…' : `${info.available} sample products available. You currently have ${info.existing_in_tenant ?? '—'} products in your catalog.`}
        </p>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2">
        <Button onClick={load} disabled={busy || loading} className="gap-2" data-testid="load-demo-catalog-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Load Demo Catalog
        </Button>
        <Button variant="outline" onClick={clear} disabled={busy || loading} className="gap-2" data-testid="clear-demo-catalog-btn">
          <Trash2 className="h-4 w-4" /> Remove Demo Products
        </Button>
        <span className="text-xs text-muted-foreground ml-2">
          Use this on a fresh install to populate the catalog quickly. Adjust prices afterwards.
        </span>
      </CardContent>
    </Card>
  );
}


// ====================== Arkesel SMS Settings Card ======================
function SmsCard() {
  const [cfg, setCfg] = useState({
    sms_enabled: false,
    api_key: '',
    sender_id: 'DutiLyne',
    low_stock_alerts_sms: false,
    expiry_alerts_sms: false,
    receipt_sms_default: false,
    marketing_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [preflight, setPreflight] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/settings/sms');
        setCfg((c) => ({ ...c, ...data }));
      } catch {
        // non-fatal — keep defaults
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      // Don't overwrite stored secret with the masked placeholder.
      if (payload.api_key === '********') delete payload.api_key;
      const { data } = await api.put('/settings/sms', payload);
      setCfg((c) => ({ ...c, ...data }));
      toast.success('SMS settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save SMS settings');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) {
      toast.error('Enter a Ghana phone number first (e.g. 0244111222)');
      return;
    }
    setTesting(true);
    try {
      const payload = { to: testPhone.trim() };
      // Use unsaved form values for the dry-run if user hasn't saved yet
      if (cfg.api_key && cfg.api_key !== '********') payload.api_key = cfg.api_key;
      if (cfg.sender_id) payload.sender_id = cfg.sender_id;
      const { data } = await api.post('/settings/sms/test', payload);
      toast.success(`Test SMS sent to ${data.to}`, { description: data.message_id ? `Arkesel ID: ${data.message_id}` : undefined });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed — check credentials and recipient');
    } finally {
      setTesting(false);
    }
  };

  const runPreflight = async () => {
    setChecking(true);
    try {
      const { data } = await api.post('/settings/sms/preflight');
      setPreflight(data);
      if (data.ready) toast.success('Pre-flight passed — you can go live');
      else toast.error('Pre-flight found blockers — see the checklist');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pre-flight check failed');
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const senderLen = (cfg.sender_id || '').length;

  return (
    <Card data-testid="sms-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" /> Arkesel SMS (Ghana)
        </CardTitle>
        <CardDescription>
          Send transactional SMS via your Arkesel account. Get your{' '}
          <a className="underline" href="https://account.arkesel.com/" target="_blank" rel="noreferrer">API Key</a>
          {' '}from Arkesel → SMS → API.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Live status */}
        <div
          className={`rounded-lg border p-3 text-xs ${
            cfg.sms_enabled && cfg.api_key
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
          data-testid="sms-status-banner"
        >
          {cfg.sms_enabled && cfg.api_key
            ? 'SMS is live — counseling slips, receipts and the digests you switch on below will send through your Arkesel account.'
            : cfg.sms_enabled
              ? 'Switched on, but no API key is saved yet — nothing will send. Paste your Arkesel API key below and Save.'
              : 'SMS is off — counseling slips and alerts will not send. Paste your Arkesel API key, turn on the master switch, then Save.'}
        </div>

        {/* Pre-flight check */}
        <div className="rounded-lg border p-3 space-y-2" data-testid="sms-preflight">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <p className="text-sm font-medium">Pre-flight check</p>
              <p className="text-xs text-muted-foreground">
                Confirms your key and credit with Arkesel and warns if your sender ID isn't approved yet — without sending anything.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={runPreflight} disabled={checking} data-testid="sms-preflight-btn">
              {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Shield className="h-3.5 w-3.5 mr-1.5" />}
              Run check
            </Button>
          </div>
          {preflight && (
            <ul className="space-y-1.5 pt-1" data-testid="sms-preflight-results">
              {preflight.checks.map((c) => {
                const Icon = c.level === 'ok' ? CheckCircle2 : c.level === 'warning' ? AlertTriangle : XCircle;
                const tone = c.level === 'ok' ? 'text-emerald-600' : c.level === 'warning' ? 'text-amber-600' : 'text-rose-600';
                return (
                  <li key={c.id} className="flex items-start gap-2 text-xs" data-testid={`sms-preflight-${c.id}`}>
                    <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${tone}`} />
                    <span>{c.message}</span>
                  </li>
                );
              })}
            </ul>
          )}
          <Link to="/sms-log" className="inline-block text-xs underline text-muted-foreground hover:text-foreground"
            data-testid="sms-log-link">
            View SMS delivery log →
          </Link>
        </div>

        {/* Master switch */}
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-sm font-medium">SMS Enabled</p>
            <p className="text-xs text-muted-foreground">Master toggle. When OFF, no SMS will be sent.</p>
          </div>
          <Switch
            checked={!!cfg.sms_enabled}
            onCheckedChange={(v) => set('sms_enabled', v)}
            data-testid="sms-enabled-switch"
          />
        </div>

        {/* Credentials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs">API Key</Label>
            <Input
              type="password"
              value={cfg.api_key}
              onChange={(e) => set('api_key', e.target.value)}
              placeholder={cfg.api_key === '********' ? '(saved — leave to keep)' : 'From Arkesel dashboard → SMS → API'}
              data-testid="sms-api-key-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Sender ID</Label>
            <Input
              value={cfg.sender_id}
              onChange={(e) => set('sender_id', e.target.value)}
              placeholder="DutiLyne"
              maxLength={11}
              data-testid="sms-sender-id-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Alphanumeric, max 11 chars ({senderLen}/11). Must be approved by Arkesel before live use.
            </p>
          </div>
          <div className="space-y-1.5 flex flex-col justify-end">
            <Label className="text-xs">Send a test SMS</Label>
            <div className="flex gap-2">
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="0244 111 222"
                className="flex-1"
                data-testid="sms-test-phone-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={sendTest}
                disabled={testing}
                className="gap-1.5 shrink-0"
                data-testid="sms-test-send-btn"
              >
                {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Test
              </Button>
            </div>
          </div>
        </div>

        {/* Use-case toggles */}
        <div className="space-y-2 pt-2">
          <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wide">Use cases</p>
          <ToggleRow
            label="POS receipt SMS"
            desc="At checkout, offer to text customers their sale total + receipt number."
            checked={!!cfg.receipt_sms_default}
            onChange={(v) => set('receipt_sms_default', v)}
            testid="sms-receipt-default-switch"
          />
          <ToggleRow
            label="Low-stock SMS digest"
            desc="Once-daily SMS to your business contact phone summarising low-stock items."
            checked={!!cfg.low_stock_alerts_sms}
            onChange={(v) => set('low_stock_alerts_sms', v)}
            testid="sms-low-stock-switch"
          />
          <ToggleRow
            label="Expiry SMS digest"
            desc="Once-daily SMS summarising products expiring within your alert window."
            checked={!!cfg.expiry_alerts_sms}
            onChange={(v) => set('expiry_alerts_sms', v)}
            testid="sms-expiry-switch"
          />
          <ToggleRow
            label="Marketing & pickup reminders (preview)"
            desc="Bulk patient SMS, prescription-ready reminders. Behind a feature flag — use cases will surface as they ship."
            checked={!!cfg.marketing_enabled}
            onChange={(v) => set('marketing_enabled', v)}
            testid="sms-marketing-switch"
          />
          <ToggleRow
            label="New patient document alerts"
            desc="Text staff who can view client records (and have a phone on their profile) when a patient uploads a document in the portal. At most one SMS per patient every 30 minutes."
            checked={cfg.patient_document_alerts_sms !== false}
            onChange={(v) => set('patient_document_alerts_sms', v)}
            testid="sms-patient-doc-switch"
          />
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="sms-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
          <span className="text-xs text-muted-foreground">
            Charges apply per SMS via your Arkesel account credit.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function ToggleRow({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <div className="pr-3 min-w-0">
        <p className="text-sm font-medium truncate">{label}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

// ====================== Platform Notifications Card (global admin only) ======================
// The SaaS operator's own Arkesel + SMTP account. Used for platform-originated
// notices and as the delivery fallback for any tenant that hasn't set up its
// own credentials yet (tenants never draw on this account/credit once they
// configure their own).
function WhatsAppCard() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState({
    whatsapp_enabled: false, access_token: '', phone_number_id: '', waba_id: '',
    display_number: '', refill_template_name: '',
  });
  const [platformCfg, setPlatformCfg] = useState({
    whatsapp_enabled: false, access_token: '', phone_number_id: '', waba_id: '',
    display_number: '', refill_template_name: '',
  });
  const [webhookVerifyToken, setWebhookVerifyToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPlatform, setSavingPlatform] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const calls = [api.get('/settings/whatsapp')];
        if (user?.is_global_admin) calls.push(api.get('/settings/platform/whatsapp'));
        const [mine, platform] = await Promise.all(calls);
        setCfg((c) => ({ ...c, ...mine.data }));
        setWebhookVerifyToken(mine.data.webhook_verify_token || '');
        if (platform) setPlatformCfg((c) => ({ ...c, ...platform.data }));
      } catch {
        // non-fatal — keep defaults
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.is_global_admin]);

  const webhookUrl = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/whatsapp/webhook`;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setPlatform = (k, v) => setPlatformCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (payload.access_token === '********') delete payload.access_token;
      const { data } = await api.put('/settings/whatsapp', payload);
      setCfg((c) => ({ ...c, ...data }));
      toast.success('WhatsApp settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save WhatsApp settings');
    } finally {
      setSaving(false);
    }
  };

  const savePlatform = async () => {
    setSavingPlatform(true);
    try {
      const payload = { ...platformCfg };
      if (payload.access_token === '********') delete payload.access_token;
      const { data } = await api.put('/settings/platform/whatsapp', payload);
      setPlatformCfg((c) => ({ ...c, ...data }));
      toast.success('Platform WhatsApp settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingPlatform(false);
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) {
      toast.error('Enter a phone number first (e.g. 0244111222)');
      return;
    }
    setTesting(true);
    try {
      const payload = { to: testPhone.trim() };
      if (cfg.access_token && cfg.access_token !== '********') payload.access_token = cfg.access_token;
      if (cfg.phone_number_id) payload.phone_number_id = cfg.phone_number_id;
      if (cfg.waba_id) payload.waba_id = cfg.waba_id;
      const { data } = await api.post('/settings/whatsapp/test', payload);
      toast.success(`Test message sent to ${data.to}`, { description: data.wa_message_id ? `wamid: ${data.wa_message_id}` : undefined });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed — this usually means the number hasn't messaged you in the last 24h, or credentials are wrong");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="whatsapp-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-emerald-600" /> WhatsApp Business (Meta Cloud API)
        </CardTitle>
        <CardDescription>
          Direct integration with Meta — automated refill reminders/marketing plus a two-way chat inbox.
          Create your app at{' '}
          <a className="underline" href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">
            developers.facebook.com/apps
          </a>, add the WhatsApp product, then copy the values below from API Setup.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          className={`rounded-lg border p-3 text-xs ${
            cfg.whatsapp_enabled && cfg.access_token
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
          data-testid="whatsapp-status-banner"
        >
          {cfg.whatsapp_enabled && cfg.access_token
            ? 'WhatsApp is live — the inbox below receives real messages and refill reminders will send for real once a template is approved.'
            : cfg.whatsapp_enabled
              ? "Switched on, but no access token is saved yet — nothing will send. Paste your token below and Save."
              : 'WhatsApp is off — only the click-to-chat link will show. Paste your credentials, turn on the switch below, then Save.'}
        </div>

        <div className="rounded-lg border p-3 space-y-1.5 bg-muted/30" data-testid="whatsapp-webhook-info">
          <p className="text-sm font-medium">Webhook — paste these into Meta's WhatsApp → Configuration page</p>
          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-xs">
            <span className="text-muted-foreground">Callback URL</span>
            <code className="break-all" data-testid="whatsapp-webhook-url">{webhookUrl}</code>
            <span className="text-muted-foreground">Verify token</span>
            <code className="break-all" data-testid="whatsapp-webhook-token">{webhookVerifyToken || '(set by the platform operator)'}</code>
          </div>
          <p className="text-[11px] text-muted-foreground">Subscribe to the <b>messages</b> webhook field after verifying.</p>
        </div>

        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-sm font-medium">WhatsApp Enabled</p>
            <p className="text-xs text-muted-foreground">Master toggle. When OFF, nothing sends through the API (the plain chat link still works).</p>
          </div>
          <Switch checked={!!cfg.whatsapp_enabled} onCheckedChange={(v) => set('whatsapp_enabled', v)} data-testid="whatsapp-enabled-switch" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs">Access Token (System User, permanent)</Label>
            <Input
              type="password" value={cfg.access_token} onChange={(e) => set('access_token', e.target.value)}
              placeholder={cfg.access_token === '********' ? '(saved — leave to keep)' : 'From Business Settings → System Users'}
              data-testid="whatsapp-access-token-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Phone Number ID</Label>
            <Input value={cfg.phone_number_id} onChange={(e) => set('phone_number_id', e.target.value)} data-testid="whatsapp-phone-number-id-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">WhatsApp Business Account ID</Label>
            <Input value={cfg.waba_id} onChange={(e) => set('waba_id', e.target.value)} data-testid="whatsapp-waba-id-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Display number</Label>
            <Input value={cfg.display_number} onChange={(e) => set('display_number', e.target.value)} placeholder="+233 24 411 1222" data-testid="whatsapp-display-number-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Refill reminder template name</Label>
            <Input value={cfg.refill_template_name} onChange={(e) => set('refill_template_name', e.target.value)}
              placeholder="e.g. refill_reminder_v1" data-testid="whatsapp-refill-template-input" />
            <p className="text-[11px] text-muted-foreground">Must already be approved in Meta's WhatsApp Manager — 2 body placeholders: medicine name, due date.</p>
          </div>
          <div className="space-y-1.5 flex flex-col justify-end md:col-span-2">
            <Label className="text-xs">Send a test message</Label>
            <div className="flex gap-2">
              <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="0244111222" data-testid="whatsapp-test-phone-input" />
              <Button variant="outline" onClick={sendTest} disabled={testing} data-testid="whatsapp-test-send-btn">
                {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Needs an open 24h window (they messaged you recently) unless you've set an approved template above.</p>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <Link to="/whatsapp-inbox" className="text-xs underline text-muted-foreground hover:text-foreground" data-testid="whatsapp-inbox-link">
            Open WhatsApp inbox →
          </Link>
          <Button onClick={save} disabled={saving} data-testid="whatsapp-save-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Save
          </Button>
        </div>

        {user?.is_global_admin && (
          <div className="border-t pt-4 space-y-3">
            <p className="text-sm font-medium">Platform shared number (fallback for tenants without their own)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Access Token</Label>
                <Input type="password" value={platformCfg.access_token} onChange={(e) => setPlatform('access_token', e.target.value)}
                  placeholder={platformCfg.access_token === '********' ? '(saved — leave to keep)' : 'System User token'}
                  data-testid="whatsapp-platform-access-token-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Phone Number ID</Label>
                <Input value={platformCfg.phone_number_id} onChange={(e) => setPlatform('phone_number_id', e.target.value)} data-testid="whatsapp-platform-phone-number-id-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">WABA ID</Label>
                <Input value={platformCfg.waba_id} onChange={(e) => setPlatform('waba_id', e.target.value)} data-testid="whatsapp-platform-waba-id-input" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch checked={!!platformCfg.whatsapp_enabled} onCheckedChange={(v) => setPlatform('whatsapp_enabled', v)} data-testid="whatsapp-platform-enabled-switch" />
                <span className="text-xs text-muted-foreground">Enabled</span>
              </div>
              <Button variant="outline" onClick={savePlatform} disabled={savingPlatform} data-testid="whatsapp-platform-save-btn">
                {savingPlatform && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Save platform settings
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function PlatformNotificationsCard() {
  const [sms, setSms] = useState({ sms_enabled: false, api_key: '', sender_id: 'DutiLyne' });
  const [smtp, setSmtp] = useState({ smtp_enabled: false, smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '', smtp_security: 'starttls', smtp_from_email: '', smtp_from_name: 'DutiLyne Alerts' });
  const [loading, setLoading] = useState(true);
  const [savingSms, setSavingSms] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [smsRes, smtpRes] = await Promise.all([
          api.get('/settings/platform/sms'),
          api.get('/settings/platform/smtp'),
        ]);
        setSms((c) => ({ ...c, ...smsRes.data }));
        setSmtp((c) => ({ ...c, ...smtpRes.data }));
      } catch {
        // non-fatal — keep defaults
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setSmsField = (k, v) => setSms((c) => ({ ...c, [k]: v }));
  const setSmtpField = (k, v) => setSmtp((c) => ({ ...c, [k]: v }));

  const saveSms = async () => {
    setSavingSms(true);
    try {
      const payload = { ...sms };
      if (payload.api_key === '********') delete payload.api_key;
      const { data } = await api.put('/settings/platform/sms', payload);
      setSms((c) => ({ ...c, ...data }));
      toast.success('Platform SMS settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingSms(false);
    }
  };

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      const payload = { ...smtp };
      if (payload.smtp_password === '********') delete payload.smtp_password;
      const { data } = await api.put('/settings/platform/smtp', payload);
      setSmtp((c) => ({ ...c, ...data }));
      toast.success('Platform SMTP settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingSmtp(false);
    }
  };

  const sendTestSms = async () => {
    if (!testPhone.trim()) {
      toast.error('Enter a Ghana phone number first (e.g. 0244111222)');
      return;
    }
    setTestingSms(true);
    try {
      const payload = { to: testPhone.trim() };
      if (sms.api_key && sms.api_key !== '********') payload.api_key = sms.api_key;
      if (sms.sender_id) payload.sender_id = sms.sender_id;
      const { data } = await api.post('/settings/platform/sms/test', payload);
      toast.success(`Test SMS sent to ${data.to}`, { description: data.message_id ? `Arkesel ID: ${data.message_id}` : undefined });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed — check credentials and recipient');
    } finally {
      setTestingSms(false);
    }
  };

  const sendTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const payload = { to_email: testEmail.trim() || undefined, ...smtp };
      if (payload.smtp_password === '********') delete payload.smtp_password;
      await api.post('/settings/platform/smtp/test', payload);
      toast.success('Test email sent');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed — check SMTP credentials');
    } finally {
      setTestingSmtp(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="platform-notifications-card">
        <CardContent className="py-10 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="platform-notifications-card" className="border-primary/30">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary" /> Platform Notifications
          <Badge variant="outline" className="text-[10px] font-normal">Global admin only</Badge>
        </CardTitle>
        <CardDescription>
          Your own DutiLyne-operator Arkesel + SMTP account. Used for platform-originated
          notices (e.g. subscription/billing) and as the delivery fallback for any tenant
          that hasn't configured their own credentials yet. Tenants that add their own
          Arkesel/SMTP account never draw on this one.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* SMS section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Platform SMS (Arkesel)</p>
              <p className="text-xs text-muted-foreground">Master toggle for the platform's own SMS fallback.</p>
            </div>
            <Switch
              checked={!!sms.sms_enabled}
              onCheckedChange={(v) => setSmsField('sms_enabled', v)}
              data-testid="platform-sms-enabled-switch"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">API Key</Label>
              <Input
                type="password"
                value={sms.api_key}
                onChange={(e) => setSmsField('api_key', e.target.value)}
                placeholder={sms.api_key === '********' ? '(saved — leave to keep)' : 'From Arkesel dashboard → SMS → API'}
                data-testid="platform-sms-api-key-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Sender ID</Label>
              <Input
                value={sms.sender_id}
                onChange={(e) => setSmsField('sender_id', e.target.value)}
                maxLength={11}
                data-testid="platform-sms-sender-id-input"
              />
            </div>
            <div className="space-y-1.5 flex flex-col justify-end">
              <div className="flex gap-2">
                <Input
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="0244 111 222"
                  className="flex-1"
                  data-testid="platform-sms-test-phone-input"
                />
                <Button variant="outline" size="sm" onClick={sendTestSms} disabled={testingSms} className="gap-1.5 shrink-0" data-testid="platform-sms-test-send-btn">
                  {testingSms ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Test
                </Button>
              </div>
            </div>
          </div>
          <Button onClick={saveSms} disabled={savingSms} size="sm" className="gap-1.5" data-testid="platform-sms-save-btn">
            {savingSms ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Platform SMS
          </Button>
        </div>

        <Separator />

        {/* SMTP section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Platform SMTP</p>
              <p className="text-xs text-muted-foreground">Master toggle for the platform's own email fallback.</p>
            </div>
            <Switch
              checked={!!smtp.smtp_enabled}
              onCheckedChange={(v) => setSmtpField('smtp_enabled', v)}
              data-testid="platform-smtp-enabled-switch"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">SMTP Host</Label>
              <Input value={smtp.smtp_host} onChange={(e) => setSmtpField('smtp_host', e.target.value)} placeholder="smtp.gmail.com" data-testid="platform-smtp-host-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Port</Label>
              <Input type="number" value={smtp.smtp_port} onChange={(e) => setSmtpField('smtp_port', Number(e.target.value))} data-testid="platform-smtp-port-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Username</Label>
              <Input value={smtp.smtp_username} onChange={(e) => setSmtpField('smtp_username', e.target.value)} data-testid="platform-smtp-username-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Password</Label>
              <Input type="password" value={smtp.smtp_password} onChange={(e) => setSmtpField('smtp_password', e.target.value)} placeholder={smtp.smtp_password === '********' ? '(saved — leave to keep)' : ''} data-testid="platform-smtp-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">From Email</Label>
              <Input value={smtp.smtp_from_email} onChange={(e) => setSmtpField('smtp_from_email', e.target.value)} data-testid="platform-smtp-from-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">From Name</Label>
              <Input value={smtp.smtp_from_name} onChange={(e) => setSmtpField('smtp_from_name', e.target.value)} data-testid="platform-smtp-from-name-input" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com (blank = your own email)"
              className="flex-1"
              data-testid="platform-smtp-test-email-input"
            />
            <Button variant="outline" size="sm" onClick={sendTestSmtp} disabled={testingSmtp} className="gap-1.5 shrink-0" data-testid="platform-smtp-test-send-btn">
              {testingSmtp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Test
            </Button>
          </div>
          <Button onClick={saveSmtp} disabled={savingSmtp} size="sm" className="gap-1.5" data-testid="platform-smtp-save-btn">
            {savingSmtp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Platform SMTP
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


